import { useCallback, useState } from 'react';

import {
  CHUNK_SIZE,
  EMPTY_TRANSCRIPT_ERROR,
  completeUpload,
  convertMediaBlobToFile,
  getPresignedUrls,
  initiateUpload,
  isUploadInProgressAtom,
  uploadFileParts,
  useMicrophoneContext,
} from '@repo/videa-notes';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAtomValue, useSetAtom } from 'jotai';

import configurations from '@/config';
import { endpoints } from '@/react-query/endpoints';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { oktaAxiosInstance } from '@/utils/api';

import { isEmptyTranscriptError } from '../helpers';
import { type VideaNotesProcessingError, VideaNotesUIState } from '../types';

interface UseAudioUploadProps {
  mediaBlobUrl: string | undefined;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  currentOrganizationId: string;
  currentPracticeId: string;
  patientConsentRecordId: string | undefined;
  selectedTemplate: string | undefined;
  /** BCP 47 language tag for the recording (e.g. 'en', 'es') */
  selectedLanguage: string;
  enableVoiceNotesConsent: boolean;
  refetchClinicalNotes: () => Promise<{ data: ClinicalNote[] | undefined }>;
  refetchStandalonePatientVisitNotes: () => Promise<{
    data: ClinicalNote[] | undefined;
  }>;
  onSetUiStatus: (status: VideaNotesUIState) => void;
  onSetIsSheetOpen: (isOpen: boolean) => void;
  onSetSelectedNoteId: (noteId: string | undefined) => void;
  onGenerateStartInitiateUploadRequest: () => void;
  onGenerateStartPresignedUrlsRequest: () => void;
  onGenerateStartUploadPartsRequest: () => void;
  onGenerateStartCompleteUploadRequest: () => void;
  onGenerateSuccess: (generationTime: number) => void;
  onUploadError: (errorMeta: UploadErrorMeta) => void;
  onUploadSuccess: (s3UploadTime: number) => void;
}

export interface UploadErrorMeta {
  step:
    | 'initiate-upload'
    | 'presigned-urls'
    | 'upload-parts'
    | 'complete-upload'
    | 'unknown';
  statusCode?: number;
  errorMessage: string;
  errorCode?: string;
  url?: string;
}

interface UseAudioUploadReturn {
  processingError: VideaNotesProcessingError | undefined;
  uploadAudio: (overrideBlobUrl?: string) => Promise<void>;
}

export const useAudioUpload = ({
  mediaBlobUrl,
  unifiedAppointmentId,
  standalonePatientVisitId,
  currentOrganizationId,
  currentPracticeId,
  patientConsentRecordId,
  selectedTemplate,
  selectedLanguage,
  enableVoiceNotesConsent,
  refetchClinicalNotes,
  refetchStandalonePatientVisitNotes,
  onSetUiStatus,
  onSetIsSheetOpen,
  onSetSelectedNoteId,
  onGenerateStartInitiateUploadRequest,
  onGenerateStartPresignedUrlsRequest,
  onGenerateStartUploadPartsRequest,
  onGenerateStartCompleteUploadRequest,
  onGenerateSuccess,
  onUploadError,
  onUploadSuccess,
}: UseAudioUploadProps): UseAudioUploadReturn => {
  const queryClient = useQueryClient();
  const setIsUploadInProgress = useSetAtom(isUploadInProgressAtom);
  const [processingError, setProcessingError] =
    useState<VideaNotesProcessingError>();

  const { devices: availableDevices, selectedDeviceId } =
    useMicrophoneContext();

  const uploadAudio = useCallback(
    async (overrideBlobUrl?: string) => {
      // If overrideBlobUrl is provided (dev file upload), skip consent check
      const isDevUpload = !!overrideBlobUrl;
      const hasRequiredConsent =
        isDevUpload || !enableVoiceNotesConsent || patientConsentRecordId;
      const effectiveBlobUrl = overrideBlobUrl ?? mediaBlobUrl;

      if (effectiveBlobUrl && hasRequiredConsent) {
        // Track current step for error reporting
        let currentStep: UploadErrorMeta['step'] = 'unknown';
        setIsUploadInProgress(true);

        try {
          const startTime = new Date();

          /* Start Multipart Upload */
          const audioFile = await convertMediaBlobToFile(
            effectiveBlobUrl,
            unifiedAppointmentId || standalonePatientVisitId,
          );

          // Create upload dependencies with axios instance for proper token refresh
          const uploadDeps = {
            apiUrl: configurations.API_URL,
            axiosInstance: oktaAxiosInstance,
          };

          // Start tracking S3 upload time
          const s3UploadStartTime = new Date();

          /**
           * step 1: initiate multipart upload
           */
          currentStep = 'initiate-upload';
          onGenerateStartInitiateUploadRequest();
          const { key, uploadId } = await initiateUpload(
            audioFile.name,
            currentOrganizationId,
            currentPracticeId,
            uploadDeps,
          );

          /**
           * step 2: get presigned URLs
           */
          currentStep = 'presigned-urls';
          onGenerateStartPresignedUrlsRequest();
          const partCount = Math.ceil(audioFile.size / CHUNK_SIZE);
          const urls = await getPresignedUrls(
            key,
            uploadId,
            partCount,
            currentOrganizationId,
            currentPracticeId,
            uploadDeps,
          );

          /**
           * step 3: upload each part
           */
          currentStep = 'upload-parts';
          onGenerateStartUploadPartsRequest();
          const etags = await uploadFileParts(audioFile, urls);

          // Calculate and track S3 upload time immediately
          const s3UploadTime =
            new Date().getTime() - s3UploadStartTime.getTime();
          onUploadSuccess(s3UploadTime);

          /**
           * step 4: complete upload
           */
          currentStep = 'complete-upload';
          onGenerateStartCompleteUploadRequest();
          const note = await completeUpload({
            key,
            uploadId,
            etags,
            patientConsentRecordId,
            unifiedAppointmentId,
            standalonePatientVisitId,
            currentOrganizationId,
            currentPracticeId,
            deps: uploadDeps,
            templateId: selectedTemplate,
            availableAudioDevices: availableDevices,
            selectedAudioDeviceId: selectedDeviceId,
            language: selectedLanguage,
          });

          // Only refetch the relevant notes based on appointment type
          let updatedNotesData: ClinicalNote[] | undefined;
          if (unifiedAppointmentId) {
            const refetchResult = await refetchClinicalNotes();
            updatedNotesData = refetchResult.data;
          }
          if (standalonePatientVisitId) {
            const refetchResult = await refetchStandalonePatientVisitNotes();
            updatedNotesData = refetchResult.data;
          }

          const generationTime = new Date().getTime() - startTime.getTime();
          onGenerateSuccess(generationTime);

          // Invalidate ambient data queries to refresh the table with new note data
          queryClient.invalidateQueries({
            queryKey: [
              endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
            ],
          });
          queryClient.invalidateQueries({
            queryKey: [
              endpoints.ambient.voiceNotes
                .batchNotesByStandalonePatientVisitIds,
            ],
          });

          // Set response - use the updated note from refetched data to ensure we have the latest data
          if (updatedNotesData && updatedNotesData.length > 0) {
            // Find the note that matches the ID from completeUpload
            const updatedNote =
              updatedNotesData.find((n) => n.id === note.id) ||
              updatedNotesData[0];
            onSetSelectedNoteId(updatedNote.id);
          } else {
            // Fallback to the note from completeUpload if refetch didn't work
            onSetSelectedNoteId(note.id);
          }

          onSetUiStatus(VideaNotesUIState.SUMMARY_RESULT);

          // Revoke the blob URL to free memory
          try {
            URL.revokeObjectURL(effectiveBlobUrl);
          } catch {
            // Ignore revoke errors
          }
        } catch (error: unknown) {
          let description = 'Please try again later.';
          let statusCode: number | undefined;
          let errorCode: string | undefined;
          let url: string | undefined;

          if (axios.isAxiosError(error)) {
            const responseData = error.response?.data;
            statusCode = error.response?.status;
            url = error.config?.url;

            // Extract error message and code from various response structures
            if (typeof responseData === 'string') {
              description = responseData;
            } else if (typeof responseData?.message === 'string') {
              description = responseData.message;
            } else if (typeof responseData?.detail === 'string') {
              description = responseData.detail;
            } else if (typeof responseData?.data?.data?.detail === 'string') {
              description = responseData.data.data.detail;
            } else if (typeof responseData?.error === 'string') {
              description = responseData.error;
            }

            // Extract error code if available
            if (typeof responseData?.code === 'string') {
              errorCode = responseData.code;
            }

            // Ensure description is always a string
            if (typeof description !== 'string') {
              description = 'Please try again later.';
            }

            // Use dedicated empty-transcript UI (422 EMPTY_TRANSCRIPT)
            if (isEmptyTranscriptError(statusCode, responseData)) {
              setProcessingError(EMPTY_TRANSCRIPT_ERROR);
              onSetUiStatus(VideaNotesUIState.ERROR);
              onUploadError({
                step: currentStep,
                statusCode,
                errorMessage: 'Audio file produced empty transcript.',
                errorCode: 'EMPTY_TRANSCRIPT',
                url,
              });
              try {
                URL.revokeObjectURL(effectiveBlobUrl);
              } catch {
                // Ignore revoke errors
              }
              return;
            }
          } else if (error instanceof Error) {
            description = error.message;
          }

          setProcessingError({
            title: 'There was an issue processing your audio recording.',
            description,
          });
          onSetUiStatus(VideaNotesUIState.ERROR);
          onUploadError({
            step: currentStep,
            statusCode,
            errorMessage: description,
            errorCode,
            url,
          });

          // Revoke blob URL on error to prevent memory buildup
          try {
            URL.revokeObjectURL(effectiveBlobUrl);
          } catch {
            // Ignore revoke errors
          }
        } finally {
          setIsUploadInProgress(false);
          onSetIsSheetOpen(true);
        }

        /* End Multipart Upload */
      } else {
        console.error('mediaBlobUrl is undefined - unable to process audio', {
          effectiveBlobUrl,
          hasRequiredConsent,
        });
      }
    },
    [
      mediaBlobUrl,
      unifiedAppointmentId,
      standalonePatientVisitId,
      currentPracticeId,
      currentOrganizationId,
      patientConsentRecordId,
      selectedTemplate,
      selectedLanguage,
      enableVoiceNotesConsent,
      availableDevices,
      selectedDeviceId,
      setIsUploadInProgress,
      queryClient,
      refetchClinicalNotes,
      refetchStandalonePatientVisitNotes,
      onSetUiStatus,
      onSetIsSheetOpen,
      onSetSelectedNoteId,
      onGenerateStartInitiateUploadRequest,
      onGenerateStartPresignedUrlsRequest,
      onGenerateStartUploadPartsRequest,
      onGenerateStartCompleteUploadRequest,
      onGenerateSuccess,
      onUploadError,
      onUploadSuccess,
    ],
  );

  return {
    processingError,
    uploadAudio,
  };
};
